// Generalized brand analysis slideshow component that displays AI insights in an engaging format
import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Play, Pause, Heart, Star, Globe, Users, Quote, Lightbulb, AlertCircle, Target, GraduationCap, ArrowRight, Crown, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import logoLarge from '@/assets/logo-large.png';

interface DetailedInsight {
  id: string;
  title: string;
  summary: string;
  strengths: {
    quote: string;
    items: string[];
  };
  opportunities: {
    quote: string;
    items: string[];
  };
  weaknesses?: {
    quote: string;
    items: string[];
  };
  elaboration: string;
}

interface AIAnalysisResponse {
  summaryInsight: string;
  detailedInsights: DetailedInsight[];
  integratedRecommendations: string[];
}

interface BrandAnalysisSlideshowProps {
  aiAnalysis: AIAnalysisResponse;
  brandName?: string;
  onClaimBrand: () => void;
  isAnonymous?: boolean;
}

const BrandAnalysisSlideshow: React.FC<BrandAnalysisSlideshowProps> = ({
  aiAnalysis,
  brandName = "Your Brand",
  onClaimBrand,
  isAnonymous = false
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [direction, setDirection] = useState(1);
  const [showQuote, setShowQuote] = useState(false);
  const [countdownProgress, setCountdownProgress] = useState(0);

  // Transform AI analysis data into slideshow format
  const slides = React.useMemo(() => {
    const transformedSlides: any[] = [];

    // Intro slide
    transformedSlides.push({
      type: 'intro',
      title: `${brandName} Brand Analysis`,
      subtitle: 'Deep AI-Powered Insights',
      content: aiAnalysis.summaryInsight,
      gradient: 'from-purple-600 to-pink-600',
      icon: Crown
    });

    // Insight slides from detailed insights
    aiAnalysis.detailedInsights.forEach((insight, index) => {
      const gradients = [
        'from-rose-500 to-pink-600',
        'from-amber-500 to-orange-600',
        'from-blue-500 to-indigo-600',
        'from-emerald-500 to-teal-600',
        'from-violet-600 to-purple-600'
      ];
      
      const icons = [Heart, Star, Globe, Users, Target];
      
      transformedSlides.push({
        type: 'insight',
        title: insight.title,
        subtitle: 'Brand Analysis Insight',
        icon: icons[index % icons.length],
        gradient: gradients[index % gradients.length],
        quote: insight.strengths.quote || insight.opportunities.quote || "Transforming your brand's potential into market success.",
        summary: insight.summary,
        strengths: insight.strengths.items,
        opportunities: insight.opportunities.items,
        weaknesses: insight.weaknesses?.items || [],
        elaboration: insight.elaboration
      });
    });

    // Recommendations slide
    if (aiAnalysis.integratedRecommendations.length > 0) {
      transformedSlides.push({
        type: 'recommendations',
        title: 'Strategic Recommendations',
        subtitle: 'Your Custom Path Forward',
        icon: Target,
        gradient: 'from-violet-600 to-purple-600',
        recommendations: aiAnalysis.integratedRecommendations
      });
    }

    // CTA slide - only for anonymous users
    if (isAnonymous) {
      transformedSlides.push({
        type: 'cta',
        title: 'Rainfactory AI Is Ready To Go!',
        subtitle: 'We have now have a PHD in your brand, and we\'re to create some amazing content for you.',
        content: 'We\'ve analyzed every aspect of your brand positioning, messaging, and market presence. Our AI has uncovered the hidden patterns and opportunities that will transform your marketing strategy.',
        gradient: 'from-emerald-600 to-blue-600',
        icon: Target,
        cta: 'Get Started'
      });
    }

    return transformedSlides;
  }, [aiAnalysis, brandName]);

  // Countdown timer for auto-advance
  useEffect(() => {
    const SLIDE_DURATION = 11000; // 11 seconds
    const FRAME_RATE = 60; // 60fps for smooth animation
    const INCREMENT = 100 / (SLIDE_DURATION / (1000 / FRAME_RATE));

    let animationFrameId: number;
    let lastTimestamp: number;

    const animate = (timestamp: number) => {
      if (!lastTimestamp) lastTimestamp = timestamp;
      const deltaTime = timestamp - lastTimestamp;

      if (deltaTime >= 1000 / FRAME_RATE) {
        setCountdownProgress(prev => {
          const newProgress = prev + INCREMENT;
          if (newProgress >= 100) {
            return 100;
          }
          return newProgress;
        });
        lastTimestamp = timestamp;
      }

      if (isPlaying && currentSlide < slides.length - 1) {
        animationFrameId = requestAnimationFrame(animate);
      }
    };

    if (isPlaying && currentSlide < slides.length - 1) {
      setCountdownProgress(0);
      animationFrameId = requestAnimationFrame(animate);

      const timer = setTimeout(() => {
        nextSlide();
      }, SLIDE_DURATION);

      return () => {
        clearTimeout(timer);
        cancelAnimationFrame(animationFrameId);
      };
    } else if (currentSlide === slides.length - 1) {
      setIsPlaying(false);
      setCountdownProgress(100);
    }

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [currentSlide, isPlaying, slides.length]);

  useEffect(() => {
    setShowQuote(false);
    const quoteTimer = setTimeout(() => {
      setShowQuote(true);
    }, 800); // Delay to allow main slide transition to complete first
    return () => clearTimeout(quoteTimer);
  }, [currentSlide]);

  const nextSlide = () => {
    setDirection(1);
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setCountdownProgress(0);
  };

  const prevSlide = () => {
    setDirection(-1);
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setCountdownProgress(0);
  };

  const goToSlide = (index: number) => {
    setDirection(index > currentSlide ? 1 : -1);
    setCurrentSlide(index);
    setCountdownProgress(0);
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const currentSlideData = slides[currentSlide];
  const IconComponent = currentSlideData.icon;

  const renderSlideContent = () => {
    if (currentSlideData.type === 'intro') {
      return (
        <div className="text-center space-y-8 max-w-4xl mx-auto">
          <div className={`inline-flex p-6 rounded-full bg-gradient-to-r ${currentSlideData.gradient} text-white shadow-2xl transform hover:scale-110 transition-transform duration-300`}>
            <IconComponent className="w-16 h-16" />
          </div>
          <h1 className={`text-6xl font-bold bg-gradient-to-r ${currentSlideData.gradient} bg-clip-text text-transparent leading-tight`}>
            {currentSlideData.title}
          </h1>
          <h2 className="text-3xl text-gray-600 font-light">
            {currentSlideData.subtitle}
          </h2>
          <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
            {currentSlideData.content}
          </p>
        </div>
      );
    }

    if (currentSlideData.type === 'recommendations') {
      return (
        <div className="max-w-5xl mx-auto space-y-8">
          <div className="text-center space-y-4">
            <div className={`inline-flex p-4 rounded-full bg-gradient-to-r ${currentSlideData.gradient} text-white shadow-xl`}>
              <IconComponent className="w-12 h-12" />
            </div>
            <h1 className={`text-5xl font-bold bg-gradient-to-r ${currentSlideData.gradient} bg-clip-text text-transparent`}>
              {currentSlideData.title}
            </h1>
            <p className="text-xl text-gray-600">{currentSlideData.subtitle}</p>
          </div>
          
                     <div className="grid md:grid-cols-2 gap-6 mt-12">
             {currentSlideData.recommendations?.map((rec: string, index: number) => (
              <div 
                key={index}
                className="group p-6 bg-white rounded-2xl shadow-lg border border-gray-100 hover:shadow-2xl hover:scale-105 transition-all duration-300"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-full bg-gradient-to-r ${currentSlideData.gradient} text-white flex items-center justify-center font-bold text-lg flex-shrink-0`}>
                    {index + 1}
                  </div>
                  <p className="text-gray-700 leading-relaxed group-hover:text-gray-900 transition-colors">
                    {rec}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (currentSlideData.type === 'cta') {
      return (
        <div className="max-w-4xl mx-auto text-center space-y-12">
          <div className="space-y-6">
            <div className={`inline-flex p-6 rounded-full bg-gradient-to-r ${currentSlideData.gradient} text-white shadow-2xl transform hover:scale-110 transition-transform duration-300`}>
              <GraduationCap className="w-16 h-16" />
            </div>
            <h1 className={`text-6xl font-bold bg-gradient-to-r ${currentSlideData.gradient} bg-clip-text text-transparent leading-tight`}>
              {currentSlideData.title}
            </h1>
            <p className="text-2xl text-gray-600 font-light max-w-2xl mx-auto">
              {currentSlideData.subtitle}
            </p>
            <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
              {currentSlideData.content}
            </p>
          </div>

          <div className="space-y-8">
            <button 
              onClick={onClaimBrand}
              className={`group relative inline-flex items-center gap-4 px-12 py-6 text-2xl font-bold text-white bg-gradient-to-r ${currentSlideData.gradient} rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 overflow-hidden`}
            >
              <span className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300"></span>
              <span className="relative z-10">{currentSlideData.cta}</span>
              <ArrowRight className="w-6 h-6 relative z-10 group-hover:translate-x-1 transition-transform duration-300" />
            </button>
          
            
            <p className="text-sm text-gray-500 mt-4">
              Free 14-day trial • Keep your analysis forever
            </p>
          </div>
        </div>
      );
    }

    // Insight slide
    return (
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Header & Quote */}
          <div className="space-y-8">
            <div className="space-y-4">
              <div className={`inline-flex p-4 rounded-full bg-gradient-to-r ${currentSlideData.gradient} text-white shadow-xl transform hover:scale-110 transition-transform duration-300`}>
                <IconComponent className="w-12 h-12" />
              </div>
              <h1 className={`text-4xl font-bold bg-gradient-to-r ${currentSlideData.gradient} bg-clip-text text-transparent leading-tight`}>
                {currentSlideData.title}
              </h1>
              <p className="text-xl text-gray-600 font-medium">{currentSlideData.subtitle}</p>
              <p className="text-lg text-gray-700 leading-relaxed">{currentSlideData.summary}</p>
            </div>

            {/* Quote */}
            <div className={`relative transition-all duration-1200 ease-out transform ${showQuote ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'}`}>
              <div className={`bg-gradient-to-r ${currentSlideData.gradient} p-6 rounded-2xl text-white shadow-2xl relative overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-3xl`}>
                <Quote className="absolute top-4 right-4 w-8 h-8 opacity-30 transition-opacity duration-300" />
                <Quote className="absolute bottom-4 left-4 w-8 h-8 opacity-30 rotate-180 transition-opacity duration-300" />
                <blockquote className="text-lg font-medium italic relative z-10">
                  "{currentSlideData.quote}"
                </blockquote>
              </div>
            </div>
          </div>

          {/* Right Column - Analysis */}
          <div className="space-y-6">
            {/* Strengths */}
            {currentSlideData.strengths && currentSlideData.strengths.length > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-2xl p-6 hover:shadow-lg transition-all duration-300">
                <h3 className="flex items-center gap-2 text-xl font-bold text-green-800 mb-4">
                  <CheckCircle2 className="w-6 h-6" />
                  Strengths
                </h3>
                <ul className="space-y-3">
                  {currentSlideData.strengths.map((strength: string, index: number) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-green-700 leading-relaxed">{strength}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Opportunities */}
            {currentSlideData.opportunities && currentSlideData.opportunities.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6 hover:shadow-lg transition-all duration-300">
                <h3 className="flex items-center gap-2 text-xl font-bold text-yellow-800 mb-4">
                  <Lightbulb className="w-6 h-6" />
                  Opportunities
                </h3>
                <ul className="space-y-3">
                  {currentSlideData.opportunities.map((opportunity: string, index: number) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-yellow-700 leading-relaxed">{opportunity}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Weaknesses */}
            {currentSlideData.weaknesses && currentSlideData.weaknesses.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-6 hover:shadow-lg transition-all duration-300">
                <h3 className="flex items-center gap-2 text-xl font-bold text-red-800 mb-4">
                  <AlertCircle className="w-6 h-6" />
                  Areas for Improvement
                </h3>
                <ul className="space-y-3">
                  {currentSlideData.weaknesses.map((weakness: string, index: number) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-red-700 leading-relaxed">{weakness}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50 backdrop-blur-sm bg-white/95">
        {/* Countdown Progress Bar */}
        <div className="h-1 bg-gray-100 relative overflow-hidden">
          <motion.div
            className={`h-full bg-gradient-to-r ${currentSlideData.gradient}`}
            initial={{ width: '0%' }}
            animate={{ width: `${countdownProgress}%` }}
            transition={{ duration: 0.1, ease: 'linear' }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4">
          {/* Mobile Layout - Two Rows */}
          <div className="md:hidden space-y-3">
            {/* First Row: Logo and Sign Up Button */}
            <div className="flex items-center justify-between">
              <a 
                href="/" 
                className="flex-shrink-0"
                title="Back to Rainfactory AI"
              >
                <img 
                  src={logoLarge} 
                  alt="Rainfactory AI" 
                  className="h-7 w-auto hover:opacity-80 transition-opacity duration-200"
                />
              </a>
              
              {isAnonymous && (
                <button
                  onClick={onClaimBrand}
                  className="group relative inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-white bg-gradient-to-r from-emerald-600 to-blue-600 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 overflow-hidden"
                >
                  <span className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300"></span>
                  <span className="relative z-10">I've seen enough, sign me up!</span>
                  <ArrowRight className="w-3 h-3 relative z-10 group-hover:translate-x-1 transition-transform duration-300" />
                </button>
              )}
            </div>
            
            {/* Second Row: Playback Controls and Indicators */}
            <div className="flex items-center justify-between">
              {/* Playback Controls */}
              <div className="flex items-center gap-2">
                <button
                  onClick={prevSlide}
                  className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors duration-200"
                >
                  <ChevronLeft className="w-4 h-4 text-gray-600" />
                </button>
                <button
                  onClick={togglePlayPause}
                  className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors duration-200"
                >
                  {isPlaying ? <Pause className="w-4 h-4 text-gray-600" /> : <Play className="w-4 h-4 text-gray-600" />}
                </button>
                <button
                  onClick={nextSlide}
                  className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors duration-200"
                >
                  <ChevronRight className="w-4 h-4 text-gray-600" />
                </button>
              </div>

              {/* Slide Indicators */}
              <div className="flex items-center gap-1.5">
                {slides.map((slide, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                      index === currentSlide 
                        ? `bg-gradient-to-r ${slide.gradient} scale-125` 
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Desktop Layout - Single Row */}
          <div className="hidden md:flex items-center justify-between">
            <div className="flex items-center gap-6">
              {/* Logo */}
              <a 
                href="/" 
                className="flex-shrink-0"
                title="Back to Rainfactory AI"
              >
                <img 
                  src={logoLarge} 
                  alt="Rainfactory AI" 
                  className="h-8 w-auto hover:opacity-80 transition-opacity duration-200"
                />
              </a>
              
              {/* Playback Controls */}
              <div className="flex items-center gap-2">
                <button
                  onClick={prevSlide}
                  className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors duration-200"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-600" />
                </button>
                <button
                  onClick={togglePlayPause}
                  className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors duration-200"
                >
                  {isPlaying ? <Pause className="w-5 h-5 text-gray-600" /> : <Play className="w-5 h-5 text-gray-600" />}
                </button>
                <button
                  onClick={nextSlide}
                  className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors duration-200"
                >
                  <ChevronRight className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>

            {/* Slide Indicators */}
            <div className="flex items-center gap-2">
              {slides.map((slide, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentSlide 
                      ? `bg-gradient-to-r ${slide.gradient} scale-125` 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>

            {isAnonymous && (
              <button
                onClick={onClaimBrand}
                className="group relative inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-emerald-600 to-blue-600 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 overflow-hidden"
              >
                <span className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300"></span>
                <span className="relative z-10">I've seen enough, sign me up!</span>
                <ArrowRight className="w-4 h-4 relative z-10 group-hover:translate-x-1 transition-transform duration-300" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative overflow-hidden">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentSlide}
            custom={direction}
            initial={{
              x: direction > 0 ? '100%' : '-100%',
              opacity: 0
            }}
            animate={{
              x: 0,
              opacity: 1
            }}
            exit={{
              x: direction > 0 ? '-100%' : '100%',
              opacity: 0
            }}
            transition={{
              x: { type: 'spring', stiffness: 300, damping: 30 },
              opacity: { duration: 0.3 }
            }}
            className="min-h-screen flex items-center justify-center px-4 md:px-6 py-8 md:py-16"
          >
            {renderSlideContent()}
          </motion.div>
        </AnimatePresence>
      </div>

       {/* Progress Bar */}
       <div className="fixed bottom-0 left-0 right-0 h-1 bg-gray-200">
         <div 
           className={`h-full bg-gradient-to-r ${currentSlideData.gradient} transition-all duration-300`}
           style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
         />
       </div>
    </div>
  );
};

export default BrandAnalysisSlideshow;